import { supabase } from '../supabase';
import { coreCall } from './gatewayService';
import { distribuisciPagatoSulleRighe } from '../utils/consultantPaymentAllocation';

// P12 (MD/architecture/provvigioni-motore-attuale-dinamiche.md §2.2/§2.4): il calcolo provvigioni
// passa SEMPRE dal motore server-side in flusia-core (worker BullMQ `commission-recalc`).
// Questo servizio è il client di LETTURA della proiezione `commission_calculations` + il gestore
// dei pagamenti persona (`commission_consultant_payments`). Nessun calcolo client-side, nessun
// fallback JS: se il core non risponde, la UI mostra la proiezione esistente — mai numeri
// ricalcolati con altre regole. Il vecchio motore JS (2.500 righe) è stato rimosso col cutover.

export interface CachedCommissionEntry {
  id: string;
  deal_id: string;
  deal_service_id?: string;  // ID univoco del servizio nell'offerta (per distinguere servizi duplicati)
  service_id?: string;
  variable_id?: string; // Variable ID for commission on confirmed variables
  person_id: string;
  person_type: 'agent' | 'referrer';
  amount: number;
  commission_rate: number;
  commission_type?: 'percentage' | 'fixed';  // Tipo di commissione (default: percentage)
  commission_amount: number;
  deal_title?: string;
  quote_number?: string;
  client_name?: string;
  client_id?: string;
  service_title?: string;
  service_type: string;
  service_category?: string;
  person_name?: string; // Nome reale della persona
  is_recurring: boolean;
  is_monthly_entry: boolean;
  month_index?: number;
  original_deal_id?: string;
  monthly_duration?: number;
  deal_closed_at?: string;
  created_at: string;
  updated_at: string;
  // Commission scaling tracking fields
  base_commission_rate?: number;
  referrer_commission_rate?: number;
  referrer_commission_type?: 'percentage' | 'fixed';  // Tipo commissione referrer per display
  scaling_applied?: boolean;
  // Payment tracking fields
  total_invoiced?: number;
  has_invoices?: boolean;  // True se ci sono fatture collegate (anche solo anticipazioni)
  total_paid?: number;
  payment_percentage?: number;
  commission_cash?: number;
  commission_outstanding?: number;
  paid_status?: boolean;
  last_paid_date?: string;
  // Consultant payment tracking fields
  total_paid_to_consultant?: number;
  last_paid_to_consultant_date?: string;
  payment_count_to_consultant?: number;
  consultant_payment_status?: 'unpaid' | 'partial' | 'full';
}

class CommissionCacheService {
  /**
   * Get cached commission calculations for a specific date
   */
  async getCachedCommissions(organizationId: string, calculationDate: Date): Promise<CachedCommissionEntry[]> {
    // CRITICAL: Normalize to first day of month for consistent database queries (UTC)
    const year = calculationDate.getFullYear();
    const month = calculationDate.getMonth();
    const normalizedDate = new Date(Date.UTC(year, month, 1));
    const dateStr = normalizedDate.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('commission_calculations')
      .select(`
        *,
        deal:deals!commission_calculations_deal_id_fkey(quote_number, signed_at),
        service:services!commission_calculations_service_id_fkey(
          service_category:service_categories(name)
        )
      `)
      .eq('organization_id', organizationId)
      .eq('calculation_date', dateStr)
      .order('deal_closed_at', { ascending: false });

    if (error) {
      console.error('[CommissionCacheService] Error fetching cached commissions:', error);
      throw error;
    }

    // Map the results to include quote_number, service_category and use signed_at from deal
    const entries = (data || []).map((row: any) => ({
      ...row,
      quote_number: row.deal?.quote_number,
      service_category: row.service?.service_category?.name,
      // Use signed_at from deal (always up-to-date) with fallback to cached deal_closed_at
      deal_closed_at: row.deal?.signed_at || row.deal_closed_at,
      // Remove the nested objects to keep interface clean
      deal: undefined,
      service: undefined
    }));

    // Recupera commission_type da deal_referrers per le entry referrer (senza migration)
    const referrerEntries = entries.filter((e: any) => e.person_type === 'referrer');
    if (referrerEntries.length > 0) {
      const dealIds = Array.from(new Set(referrerEntries.map((e: any) => e.deal_id)));

      const { data: dealReferrersData } = await supabase
        .from('deal_referrers')
        .select('deal_id, referrer_id, commission_type')
        .in('deal_id', dealIds);

      if (dealReferrersData) {
        const typeMap = new Map<string, string>();
        dealReferrersData.forEach((dr: any) => {
          typeMap.set(`${dr.deal_id}_${dr.referrer_id}`, dr.commission_type || 'percentage');
        });

        entries.forEach((entry: any) => {
          if (entry.person_type === 'referrer') {
            const key = `${entry.deal_id}_${entry.person_id}`;
            entry.commission_type = typeMap.get(key) || 'percentage';
          }
        });
      }
    }

    // FIX: Recupera commission_type e commission_fixed_amount da deal_user_commission_overrides per le entry agent
    const agentEntries = entries.filter((e: any) => e.person_type === 'agent');
    if (agentEntries.length > 0) {
      const dealIds = Array.from(new Set(agentEntries.map((e: any) => e.deal_id)));

      // FIX: La tabella deal_user_commission_overrides è SOLO per utenti,
      // NON ha una colonna referrer_id - la condizione .is('referrer_id', null) falliva
      const { data: userOverridesData } = await supabase
        .from('deal_user_commission_overrides')
        .select('deal_id, user_id, commission_type, commission_fixed_amount')
        .in('deal_id', dealIds);

      if (userOverridesData) {
        const overrideMap = new Map<string, { type: string; fixedAmount: number | null }>();
        userOverridesData.forEach((uo: any) => {
          overrideMap.set(`${uo.deal_id}_${uo.user_id}`, {
            type: uo.commission_type || 'percentage',
            fixedAmount: uo.commission_fixed_amount
          });
        });

        entries.forEach((entry: any) => {
          if (entry.person_type === 'agent') {
            const key = `${entry.deal_id}_${entry.person_id}`;
            const override = overrideMap.get(key);
            if (override) {
              entry.commission_type = override.type;
              // Per commissioni fisse, aggiornare tutti i campi rate per display corretto
              if (override.type === 'fixed' && override.fixedAmount !== null) {
                entry.commission_rate = override.fixedAmount;
                entry.base_commission_rate = override.fixedAmount; // FIX: anche base per evitare "0" extra
              }
            }
          }
        });
      }
    }

    // Arricchisci con has_invoices: verifica quali deal hanno fatture collegate
    // Necessario perché total_invoiced=0 sia per "nessuna fattura" sia per "solo anticipazioni"
    const allDealIds = Array.from(new Set(entries.map((e: any) => e.deal_id).filter(Boolean)));
    if (allDealIds.length > 0) {
      const { data: invoicedDeals } = await supabase
        .from('fattureincloud_invoices')
        .select('deal_id')
        .in('deal_id', allDealIds)
        .not('status', 'in', '("cancelled","deleted")');

      const dealIdsWithInvoices = new Set(
        (invoicedDeals || []).map((inv: any) => inv.deal_id)
      );

      entries.forEach((entry: any) => {
        entry.has_invoices = dealIdsWithInvoices.has(entry.deal_id);
      });
    }

    // FIX: Deduplica per chiave logica, mantenendo l'entry più recente (updated_at)
    // FIX 2026-07-10 (F-06 Finup): la chiave usa deal_service_id (se presente) per NON collassare
    // servizi duplicati (stesso service_id su più righe dell'offerta). Fallback a service_id
    // solo per entry legacy senza deal_service_id.
    const dedupMap = new Map<string, any>();
    for (const entry of entries) {
      const serviceKey = entry.deal_service_id ?? entry.service_id ?? 'null';
      const key = `${entry.deal_id}_${serviceKey}_${entry.variable_id ?? 'null'}_${entry.person_id}_${entry.person_type}_${entry.month_index ?? 'null'}`;
      const existing = dedupMap.get(key);
      if (!existing || new Date(entry.updated_at) > new Date(existing.updated_at)) {
        // Se l'entry esistente ha dati di pagamento, preservali
        if (existing && (existing.total_paid_to_consultant ?? 0) > 0 && (entry.total_paid_to_consultant ?? 0) === 0) {
          dedupMap.set(key, {
            ...entry,
            total_paid_to_consultant: existing.total_paid_to_consultant,
            payment_count_to_consultant: existing.payment_count_to_consultant,
            last_paid_to_consultant_date: existing.last_paid_to_consultant_date,
            consultant_payment_status: existing.consultant_payment_status
          });
        } else {
          dedupMap.set(key, entry);
        }
      } else if ((entry.total_paid_to_consultant ?? 0) > 0 && (existing.total_paid_to_consultant ?? 0) === 0) {
        // L'entry corrente ha dati di pagamento che quella esistente non ha
        dedupMap.set(key, {
          ...existing,
          total_paid_to_consultant: entry.total_paid_to_consultant,
          payment_count_to_consultant: entry.payment_count_to_consultant,
          last_paid_to_consultant_date: entry.last_paid_to_consultant_date,
          consultant_payment_status: entry.consultant_payment_status
        });
      }
    }

    const dedupedEntries = Array.from(dedupMap.values());
    if (dedupedEntries.length !== entries.length) {
      console.warn(`[CommissionCacheService] getCachedCommissions: deduplicati ${entries.length - dedupedEntries.length} duplicati (${entries.length} → ${dedupedEntries.length})`);
    }

    return dedupedEntries;
  }

  /**
   * Attende che il worker `commission-recalc` del core abbia finito per l'org (polling su
   * GET /api/commissions/status, stati BullMQ: waiting/active/delayed = in corso).
   * Best-effort: al timeout o su errore di rete ritorna comunque — la UI leggerà la proiezione
   * esistente (§2.4: mai bloccare la pagina, mai calcolare con altre regole).
   */
  private async waitForRecalc(organizationId: string, timeoutMs: number = 45_000): Promise<void> {
    const startedAt = Date.now();
    const pollIntervalMs = 1_500;

    // Piccola attesa iniziale: il job viene preso in carico dal worker quasi subito
    await new Promise(resolve => setTimeout(resolve, 500));

    while (Date.now() - startedAt < timeoutMs) {
      try {
        const { data, error } = await coreCall<{ status: string }>(
          `commissions/status?organization_id=${organizationId}`,
          { method: 'GET' }
        );
        if (error) return; // status non disponibile: non bloccare la UI
        const status = data?.status;
        if (status !== 'waiting' && status !== 'active' && status !== 'delayed') {
          return; // completed / idle / failed / unknown → il worker non sta più lavorando
        }
      } catch {
        return;
      }
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }
  }

  /**
   * Richiede al motore server-side il ricalcolo dell'org e attende (best-effort) che il worker
   * abbia scritto la proiezione, così la lettura successiva vede i numeri aggiornati.
   * Il ricalcolo è sempre org-wide: chiamarlo una volta copre tutti i mesi/deal (§2.2).
   * La firma mantiene i parametri storici (calculationDate/triggeredBy/force) per compatibilità
   * con i call-site, ma il core ricalcola sempre tutto e il debounce è server-side (jobId per org).
   */
  async triggerCalculation(organizationId: string, _calculationDate?: Date, _triggeredBy?: string, _forceRecalculation: boolean = false): Promise<void> {
    const { error: coreError } = await coreCall('commissions/recalc', {
      body: { organization_id: organizationId }
    });

    if (coreError) {
      throw new Error(coreError.message || 'Ricalcolo non disponibile, riprova');
    }

    await this.waitForRecalc(organizationId);
  }

  /**
   * Mark a commission as paid to consultant/referrer
   * Creates a payment record for tracking partial/full payments
   */
  async markCommissionAsPaidToConsultant(params: {
    organizationId: string;
    dealId: string;
    serviceId: string | null;
    /**
     * Riga di servizio dell'offerta. È il riferimento CORRETTO: `serviceId` è il servizio a
     * catalogo e non distingue due righe della stessa offerta che puntano allo stesso servizio
     * (caso MOVEO 048/2026). Chi chiama deve passarlo sempre, quando la provvigione nasce da una
     * riga di servizio; resta null solo per variabili e provvigioni a livello di offerta.
     */
    dealServiceId?: string | null;
    variableId?: string | null;
    personId: string;
    personType: 'agent' | 'referrer';
    calculationDate: Date;
    monthIndex: number | null;
    amountPaid: number;
    paymentMethod?: string;
    referenceNumber?: string;
    notes?: string;
    paidAt?: Date; // Data pagamento opzionale (default: now)
  }): Promise<void> {
    // CRITICAL: Normalize to first day of month for consistent database queries (UTC)
    const year = params.calculationDate.getFullYear();
    const month = params.calculationDate.getMonth();
    const normalizedDate = new Date(Date.UTC(year, month, 1));
    const dateStr = normalizedDate.toISOString().split('T')[0];

    try {
      // Get current user for audit trail
      const { data: userData } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('commission_consultant_payments')
        .insert({
          organization_id: params.organizationId,
          deal_id: params.dealId,
          service_id: params.serviceId,
          deal_service_id: params.dealServiceId || null,
          variable_id: params.variableId || null,
          person_id: params.personId,
          person_type: params.personType,
          calculation_date: dateStr,
          month_index: params.monthIndex,
          amount_paid: params.amountPaid,
          paid_by: userData.user?.id,
          paid_at: params.paidAt ? params.paidAt.toISOString() : new Date().toISOString(),
          payment_method: params.paymentMethod,
          reference_number: params.referenceNumber,
          notes: params.notes
        });

      if (error) {
        console.error('[CommissionCacheService] Error marking commission as paid:', error);
        throw error;
      }

      console.log(`[CommissionCacheService] Marked commission as paid: ${params.amountPaid} to ${params.personType} ${params.personId}`);

      // Aggiorna anche la proiezione commission_calculations con i nuovi totali di pagamento
      // (l'UPDATE dei soli campi consultant_* è l'unica scrittura client consentita sulla proiezione)
      await this.updateConsultantPaymentCache({
        organizationId: params.organizationId,
        dealId: params.dealId,
        serviceId: params.serviceId,
        dealServiceId: params.dealServiceId || null,
        variableId: params.variableId || null,
        personId: params.personId,
        personType: params.personType,
        calculationDate: dateStr,
        monthIndex: params.monthIndex
      });
    } catch (error) {
      console.error('[CommissionCacheService] Error in markCommissionAsPaidToConsultant:', error);
      throw error;
    }
  }

  /**
   * Helper: Aggiorna i campi di pagamento consulente nella proiezione commission_calculations
   * ricalcolando i totali dalla tabella pagamenti (fonte di verità).
   */
  private async updateConsultantPaymentCache(params: {
    organizationId: string;
    dealId: string;
    serviceId: string | null;
    dealServiceId?: string | null;
    variableId: string | null;
    personId: string;
    personType: 'agent' | 'referrer';
    calculationDate: string;
    monthIndex: number | null;
  }): Promise<void> {
    try {
      // 1. Recupera tutti i pagamenti per questa commissione dalla tabella commission_consultant_payments
      let paymentQuery = supabase
        .from('commission_consultant_payments')
        .select('amount_paid, paid_at, deal_service_id')
        .eq('organization_id', params.organizationId)
        .eq('deal_id', params.dealId)
        .eq('person_id', params.personId)
        .eq('person_type', params.personType);

      // Gestione campi nullable
      if (params.serviceId) {
        paymentQuery = paymentQuery.eq('service_id', params.serviceId);
      } else {
        paymentQuery = paymentQuery.is('service_id', null);
      }

      if (params.variableId) {
        paymentQuery = paymentQuery.eq('variable_id', params.variableId);
      } else {
        paymentQuery = paymentQuery.is('variable_id', null);
      }

      if (params.monthIndex !== null && params.monthIndex !== undefined) {
        paymentQuery = paymentQuery.eq('month_index', params.monthIndex);
      } else {
        paymentQuery = paymentQuery.is('month_index', null);
      }

      const { data: payments, error: paymentError } = await paymentQuery;

      if (paymentError) {
        console.error('[CommissionCacheService] Error fetching payment summary:', paymentError);
        return; // Non lanciamo errore - il pagamento è stato salvato, l'aggiornamento proiezione è secondario
      }

      // 2. Calcola i totali, tenendo separate le due epoche dei pagamenti:
      //    - quelli con `deal_service_id` valorizzato appartengono a UNA riga precisa
      //    - quelli storici (colonna aggiunta il 12/08/2026) non lo sanno e vanno ripartiti
      const perRiga = new Map<string, number>();
      let daRipartire = 0;
      for (const p of payments || []) {
        const importo = p.amount_paid || 0;
        const riga = (p as { deal_service_id?: string | null }).deal_service_id;
        if (riga) perRiga.set(riga, (perRiga.get(riga) || 0) + importo);
        else daRipartire += importo;
      }
      const totalePagato = (payments || []).reduce((somma, p) => somma + (p.amount_paid || 0), 0);
      const paymentCount = payments?.length || 0;
      const lastPaidDate = payments && payments.length > 0
        ? payments.reduce((max, p) => (p.paid_at > max ? p.paid_at : max), payments[0].paid_at)
        : null;

      // 3. Recupera commission_amount dalla proiezione per determinare lo status
      // Il mese salvato sul pagamento segue la griglia di competenza di quando è stato registrato
      // e può non coincidere più con quello della riga: se col mese non si trova nulla si riprova
      // senza. Prima si usciva in silenzio e, cancellato il pagamento, la riga restava "pagata"
      // (Finup 29/09/2026: vecchio segnalatore rimasto in scheda dopo il cambio sull'offerta).
      const cercaRighe = (conMese: boolean) => {
        let cacheQuery = supabase
          .from('commission_calculations')
          .select('id, deal_id, deal_service_id, commission_amount, commission_cash, total_invoiced, calculation_date, month_index')
          .eq('organization_id', params.organizationId)
          .eq('deal_id', params.dealId)
          .eq('person_id', params.personId)
          .eq('person_type', params.personType);
        if (conMese) cacheQuery = cacheQuery.eq('calculation_date', params.calculationDate);

        // Gestione campi nullable per la query
        if (params.serviceId) {
          cacheQuery = cacheQuery.eq('service_id', params.serviceId);
        } else {
          cacheQuery = cacheQuery.is('service_id', null);
        }

        if (params.monthIndex !== null && params.monthIndex !== undefined) {
          cacheQuery = cacheQuery.eq('month_index', params.monthIndex);
        } else {
          cacheQuery = cacheQuery.is('month_index', null);
        }

        if (params.variableId) {
          cacheQuery = cacheQuery.eq('variable_id', params.variableId);
        } else {
          cacheQuery = cacheQuery.is('variable_id', null);
        }

        return cacheQuery;
      };

      // FIX 2026-08-12 (MOVEO SRL, offerta 048/2026): qui c'era `.single()`. La chiave del
      // pagamento usa il servizio a CATALOGO, quindi un'offerta con due righe sullo stesso
      // servizio fa combaciare DUE righe di proiezione: `.single()` andava in errore e la
      // funzione usciva dal warning "may be normal", lasciando `total_paid_to_consultant` a
      // zero in database su entrambe. Ora si prendono tutte le righe e si ripartisce.
      let { data: righeProiezione, error: cacheError } = await cercaRighe(true);
      if (!cacheError && (!righeProiezione || righeProiezione.length === 0)) {
        ({ data: righeProiezione, error: cacheError } = await cercaRighe(false));
      }

      if (cacheError || !righeProiezione || righeProiezione.length === 0) {
        console.warn('[CommissionCacheService] Projection row not found for payment update, may be normal:', cacheError?.message);
        return;
      }

      // 4. Determina la base pagabile
      // Verifica se il deal ha fatture collegate (anche solo anticipazioni)
      const { count: invoiceCount } = await supabase
        .from('fattureincloud_invoices')
        .select('id', { count: 'exact', head: true })
        .eq('deal_id', righeProiezione[0].deal_id)
        .not('status', 'in', '("cancelled","deleted")');

      const hasInvoices = (invoiceCount || 0) > 0;

      // Se ci sono fatture collegate (anche solo anticipazioni), usa commission_cash
      // Se non ci sono fatture, usa commission_amount (100% pagabile)
      const pagabile = (r: { commission_amount: number | null; commission_cash: number | null }) =>
        hasInvoices ? (r.commission_cash || 0) : (r.commission_amount || 0);

      // 5. Ogni riga prende PRIMA quello che le è stato attribuito esplicitamente; solo il
      //    residuo storico senza riga viene ripartito, e sulla capienza rimasta.
      const chiaveGruppo = `${params.dealId}_${params.serviceId ?? 'null'}_${params.variableId ?? 'null'}_${params.personId}_${params.personType}_${params.monthIndex ?? 'null'}`;
      const quote = daRipartire > 0
        ? distribuisciPagatoSulleRighe(
            righeProiezione.map(r => ({
              deal_id: params.dealId,
              service_id: params.serviceId,
              variable_id: params.variableId,
              deal_service_id: r.deal_service_id,
              person_id: params.personId,
              is_agent: params.personType === 'agent',
              month_index: r.month_index,
              calculation_date: r.calculation_date,
              commission_amount: Math.max(0, pagabile(r) - (perRiga.get(r.deal_service_id ?? '') || 0))
            })),
            new Map([[chiaveGruppo, { total: daRipartire, count: paymentCount }]])
          )
        : new Map();

      for (let i = 0; i < righeProiezione.length; i++) {
        const attribuito = perRiga.get(righeProiezione[i].deal_service_id ?? '') || 0;
        const quotaPagata = Math.round((attribuito + (quote.get(i)?.pagato ?? 0)) * 100) / 100;
        const newStatus: 'unpaid' | 'partial' | 'full' =
          quotaPagata <= 0 ? 'unpaid'
            : quotaPagata >= pagabile(righeProiezione[i]) - 0.01 ? 'full'
            : 'partial';

        const { error: updateError } = await supabase
          .from('commission_calculations')
          .update({
            total_paid_to_consultant: quotaPagata,
            last_paid_to_consultant_date: quotaPagata > 0 ? lastPaidDate : null,
            payment_count_to_consultant: quotaPagata > 0 ? paymentCount : 0,
            consultant_payment_status: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', righeProiezione[i].id);

        if (updateError) {
          console.error('[CommissionCacheService] Error updating projection with payment totals:', updateError);
          return;
        }
      }

      console.log(`[CommissionCacheService] Projection updated: total_paid=${totalePagato} ripartito su ${righeProiezione.length} righe, count=${paymentCount}`);
    } catch (error) {
      // Non propaghiamo l'errore - il pagamento principale è stato salvato
      console.error('[CommissionCacheService] Error in updateConsultantPaymentCache:', error);
    }
  }

  /**
   * Mark multiple commissions as paid in bulk
   */
  async markMultipleCommissionsAsPaid(params: {
    organizationId: string;
    commissions: Array<{
      dealId: string;
      serviceId: string | null;
      /** Riga di servizio dell'offerta: vedi markCommissionAsPaidToConsultant. */
      dealServiceId?: string | null;
      variableId: string | null;
      personId: string;
      personType: 'agent' | 'referrer';
      calculationDate: Date;
      monthIndex: number | null;
      amountPaid: number;
    }>;
    paymentDate: Date;
    paymentMethod?: string;
    referenceNumber?: string;
    notes?: string;
  }): Promise<void> {
    try {
      // Get current user for audit trail
      const { data: userData } = await supabase.auth.getUser();

      // Prepare all records to insert
      const records = params.commissions.map(c => {
        // CRITICAL: Normalize to first day of month for consistent database queries (UTC)
        const year = c.calculationDate.getFullYear();
        const month = c.calculationDate.getMonth();
        const normalizedDate = new Date(Date.UTC(year, month, 1));

        return {
          organization_id: params.organizationId,
          deal_id: c.dealId,
          service_id: c.serviceId,
          deal_service_id: c.dealServiceId || null,
          variable_id: c.variableId,
          person_id: c.personId,
          person_type: c.personType,
          calculation_date: normalizedDate.toISOString().split('T')[0],
          month_index: c.monthIndex,
          amount_paid: c.amountPaid,
          paid_by: userData.user?.id,
          paid_at: params.paymentDate.toISOString(),
          payment_method: params.paymentMethod,
          reference_number: params.referenceNumber,
          notes: params.notes
        };
      });

      // Insert batch
      const { error } = await supabase
        .from('commission_consultant_payments')
        .insert(records);

      if (error) {
        console.error('[CommissionCacheService] Error in bulk payment:', error);
        throw error;
      }

      // Aggiorna la proiezione per ogni commissione pagata
      // Usiamo Promise.allSettled per non bloccare se un aggiornamento fallisce
      const cacheUpdates = params.commissions.map(c => {
        const year = c.calculationDate.getFullYear();
        const month = c.calculationDate.getMonth();
        const normalizedDate = new Date(Date.UTC(year, month, 1));
        const dateStr = normalizedDate.toISOString().split('T')[0];

        return this.updateConsultantPaymentCache({
          organizationId: params.organizationId,
          dealId: c.dealId,
          serviceId: c.serviceId,
          dealServiceId: c.dealServiceId || null,
          variableId: c.variableId,
          personId: c.personId,
          personType: c.personType,
          calculationDate: dateStr,
          monthIndex: c.monthIndex
        });
      });

      await Promise.allSettled(cacheUpdates);

      console.log(`[CommissionCacheService] Bulk payment completed: ${params.commissions.length} commissions paid`);
    } catch (error) {
      console.error('[CommissionCacheService] Error in markMultipleCommissionsAsPaid:', error);
      throw error;
    }
  }

  /**
   * Get payment history for a specific commission
   */
  async getConsultantPaymentHistory(params: {
    organizationId: string;
    dealId: string;
    serviceId: string | null;
    variableId?: string | null;
    personId: string;
    personType: 'agent' | 'referrer';
    calculationDate: Date;
    monthIndex: number | null;
  }): Promise<any[]> {
    try {
      const query = supabase
        .from('commission_consultant_payments')
        .select(`
          *,
          paid_by_user:user_profiles!commission_consultant_payments_paid_by_fkey(
            id,
            full_name,
            email
          )
        `)
        .eq('organization_id', params.organizationId)
        .eq('deal_id', params.dealId)
        .eq('person_id', params.personId)
        .eq('person_type', params.personType)
        .order('paid_at', { ascending: false });

      // Handle service_id (can be null)
      if (params.serviceId) {
        query.eq('service_id', params.serviceId);
      } else {
        query.is('service_id', null);
      }

      // Handle variable_id (can be null)
      if (params.variableId) {
        query.eq('variable_id', params.variableId);
      } else {
        query.is('variable_id', null);
      }

      // Handle month_index (can be null)
      if (params.monthIndex !== null) {
        query.eq('month_index', params.monthIndex);
      } else {
        query.is('month_index', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[CommissionCacheService] Error fetching payment history:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('[CommissionCacheService] Error in getConsultantPaymentHistory:', error);
      throw error;
    }
  }

  /**
   * Delete a consultant payment (for corrections/mistakes)
   */
  async deleteConsultantPayment(paymentId: string): Promise<void> {
    try {
      // Prima recupera le informazioni del pagamento per poter aggiornare la proiezione dopo
      const { data: paymentData, error: fetchError } = await supabase
        .from('commission_consultant_payments')
        .select('organization_id, deal_id, service_id, variable_id, person_id, person_type, calculation_date, month_index')
        .eq('id', paymentId)
        .single();

      if (fetchError) {
        console.error('[CommissionCacheService] Error fetching payment before delete:', fetchError);
        throw fetchError;
      }

      // Elimina il pagamento
      const { error } = await supabase
        .from('commission_consultant_payments')
        .delete()
        .eq('id', paymentId);

      if (error) {
        console.error('[CommissionCacheService] Error deleting payment:', error);
        throw error;
      }

      console.log(`[CommissionCacheService] Deleted payment: ${paymentId}`);

      // Aggiorna la proiezione con i nuovi totali (dopo la cancellazione)
      if (paymentData) {
        await this.updateConsultantPaymentCache({
          organizationId: paymentData.organization_id,
          dealId: paymentData.deal_id,
          serviceId: paymentData.service_id,
          variableId: paymentData.variable_id,
          personId: paymentData.person_id,
          personType: paymentData.person_type as 'agent' | 'referrer',
          calculationDate: paymentData.calculation_date,
          monthIndex: paymentData.month_index
        });
      }
    } catch (error) {
      console.error('[CommissionCacheService] Error in deleteConsultantPayment:', error);
      throw error;
    }
  }

  /**
   * Get total amount paid to consultant for a commission (sum of all payments)
   */
  async getConsultantPaymentTotal(params: {
    organizationId: string;
    dealId: string;
    serviceId: string | null;
    personId: string;
    personType: 'agent' | 'referrer';
    calculationDate: Date;
    monthIndex: number | null;
  }): Promise<number> {
    const payments = await this.getConsultantPaymentHistory(params);
    return payments.reduce((sum, payment) => sum + (payment.amount_paid || 0), 0);
  }

  /**
   * Ricalcolo di una singola riga: chiede al core il ricalcolo dell'org (il worker copre sempre
   * tutti i deal, §2.2), attende il completamento e rilegge la riga per il diff old→new in UI.
   * Nessuna RPC client-side, nessun UPDATE diretto sulla proiezione.
   */
  async recalculateSingleRow(params: {
    commissionId: string;
    organizationId: string;
    dealId: string;
    calculationDate: string; // ISO date string (first day of month)
    personId: string;
    personType: string;
    serviceId?: string | null;
    variableId?: string | null;
    monthIndex?: number | null;
  }): Promise<{
    success: boolean;
    error?: string;
    oldValues?: { commission_amount: number; total_invoiced: number; total_paid: number; commission_cash: number; commission_outstanding: number };
    newValues?: { commission_amount: number; total_invoiced: number; total_paid: number; commission_cash: number; commission_outstanding: number };
    hasPayments: boolean;
  }> {
    try {
      const { data: currentRow, error: fetchError } = await supabase
        .from('commission_calculations')
        .select('commission_amount, total_invoiced, total_paid, commission_cash, commission_outstanding, consultant_payment_status, total_paid_to_consultant')
        .eq('id', params.commissionId)
        .single();

      if (fetchError || !currentRow) {
        return { success: false, error: 'Riga commissione non trovata', hasPayments: false };
      }

      const hasPayments = currentRow.consultant_payment_status === 'partial' || currentRow.consultant_payment_status === 'full';
      const oldValues = {
        commission_amount: Number(currentRow.commission_amount || 0),
        total_invoiced: Number(currentRow.total_invoiced || 0),
        total_paid: Number(currentRow.total_paid || 0),
        commission_cash: Number(currentRow.commission_cash || 0),
        commission_outstanding: Number(currentRow.commission_outstanding || 0),
      };

      const { error: coreError } = await coreCall('commissions/recalc', {
        body: { organization_id: params.organizationId, deal_id: params.dealId }
      });

      if (coreError) {
        return { success: false, error: coreError.message || 'Ricalcolo non disponibile, riprova', hasPayments, oldValues };
      }

      // Attende che il worker abbia scritto la proiezione, così il refetch vede i valori nuovi
      await this.waitForRecalc(params.organizationId);

      const { data: refreshedRow, error: refetchError } = await supabase
        .from('commission_calculations')
        .select('commission_amount, total_invoiced, total_paid, commission_cash, commission_outstanding')
        .eq('id', params.commissionId)
        .single();

      if (refetchError || !refreshedRow) {
        return { success: false, error: 'Proiezione non disponibile dopo il ricalcolo', hasPayments, oldValues };
      }

      const newValues = {
        commission_amount: Number(refreshedRow.commission_amount || 0),
        total_invoiced: Number(refreshedRow.total_invoiced || 0),
        total_paid: Number(refreshedRow.total_paid || 0),
        commission_cash: Number(refreshedRow.commission_cash || 0),
        commission_outstanding: Number(refreshedRow.commission_outstanding || 0),
      };

      return { success: true, oldValues, newValues, hasPayments };
    } catch (error: any) {
      console.error('[CommissionCacheService] recalculateSingleRow error:', error);
      return { success: false, error: error.message || 'Ricalcolo non disponibile, riprova', hasPayments: false };
    }
  }
}

export const commissionCacheService = new CommissionCacheService();
